import React, { useState } from "react";
import * as translator from "../../../utils/translator";
import Logo from "../../../images/logo-dark.svg";
import { useShellId } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

// HL-12294: pen + scored tablet, the whole Figma frame 5713:19884 exported @2x (713 x 554.5; the art
// overflows the frame top by 8px). Its side fades are white, so it sits on a white page.
const LOADING_MEDICATION_IMAGE =
  "https://images.doktorabc.com/redesign_2025/care-plus/wl-loading-medication/wl-loading-medication.png";

const PROGRESS_MS = 8000;

// The loader remounts at every hand-off (questionnaire submit → choose-treatment router →
// treatment page). Resume the bar from where it was instead of restarting it each time.
let progressStartedAt = 0;
const progressElapsedMs = (): number => {
  const now = Date.now();
  if (now - progressStartedAt > PROGRESS_MS) progressStartedAt = now;
  return now - progressStartedAt;
};

interface LoadingPageProps {
  language?: string;
  /**
   * HL-12357 (WL3 / V3): on mobile the heading + bar sit in the middle of the screen, the logo is
   * taken out of the flow and the art fills the space below. Desktop keeps the Figma layout.
   */
  centerContent?: boolean;
}

// Mobile: logo on top; heading, bar and art are one group centred in the space below it. The art is
// 120% of the viewport, so only its white side fades run off screen.
// md+: Figma Weight-Loss 5713:19880. Top-anchored: logo at 36px, heading at 155px, bar 16px under it,
// then the 713px art.
const LoadingPage: React.FC<LoadingPageProps> = ({ language, centerContent = false }) => {
  const pageId = useShellId("wl2_loading_page");
  const [progressDelayMs] = useState(() => -progressElapsedMs());
  const art = (
    <img
      src={LOADING_MEDICATION_IMAGE}
      alt=""
      className="mt-6 block h-auto w-[120%] max-w-none shrink-0 md:mt-[79px] md:ml-[calc(50%-360px)] md:w-[713px] md:self-start"
    />
  );

  return (
    <div id={pageId} className={`flex min-h-screen min-h-dvh flex-col bg-white overflow-x-clip ${centerContent ? "relative" : ""}`}>
      <style>{`@keyframes wl-loading-progress { from { width: 13px; } to { width: 92%; } }`}</style>
      <img
        src={Logo}
        alt="DoktorABC"
        className={`mx-auto mt-4 block h-auto w-[135px] shrink-0 md:mt-[36px] ${
          centerContent ? "max-md:absolute max-md:inset-x-0 max-md:top-0" : ""
        }`}
      />

      <div
        className={
          centerContent
            ? "grid flex-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] justify-items-center md:flex md:flex-none md:flex-col md:items-center md:justify-start"
            : "flex flex-1 flex-col items-center justify-center py-8 md:flex-none md:justify-start md:py-0"
        }
      >
        {centerContent && <div aria-hidden="true" className="md:hidden" />}
        <div className="flex w-[290px] max-w-[calc(100%-48px)] flex-col items-center text-center md:mt-[97px]">
          <h1 className="m-0 font-['Poppins'] text-[24px] font-normal leading-[1.3] text-[#08080a]">
            {t(language, "loading_heading")}
          </h1>
          <div className="mt-4 h-[7px] w-[197px] max-w-full overflow-hidden rounded-[9px] bg-[#ebf0f7]">
            <div
              className="h-full rounded-[9px]"
              style={{
                backgroundImage: "linear-gradient(185.32deg, #ffb909 0.8%, #e17200 94.83%)",
                animation: `wl-loading-progress ${PROGRESS_MS}ms cubic-bezier(0.1, 0.6, 0.3, 1) ${progressDelayMs}ms forwards`,
              }}
            />
          </div>
        </div>

        {centerContent ? (
          // The art row clips at the bottom rather than pushing the heading off centre.
          <div className="flex min-h-0 w-full flex-col items-center overflow-hidden md:contents">{art}</div>
        ) : (
          art
        )}
      </div>
    </div>
  );
};

export default LoadingPage;
